/**
 * ThreadsSidebar Component
 * Shows agent info and thread history for chat navigation
 */

import { useState, useEffect, useRef } from 'preact/hooks';
import { route } from 'preact-router';
import { threadsService } from '../services/threads.service';
import { useScopeContext } from '../contexts/ScopeContext';

// Thread type from API response
interface ThreadItem {
  threadId: string;
  resourceId: string;
  agentId?: string;
  title?: string;
  lastMessage?: string;
  lastActivity: string;
  messageCount: number;
  createdAt: string;
}

interface ThreadsSidebarProps {
  agentId: string;
  agentName?: string;
  agentRole?: string;
  currentThreadId?: string;
  refreshTrigger?: number;
}

interface GroupedThreads {
  today: ThreadItem[];
  yesterday: ThreadItem[];
  last7Days: ThreadItem[];
  last30Days: ThreadItem[];
}

export function ThreadsSidebar({ agentId, agentName: _agentName, agentRole: _agentRole, currentThreadId, refreshTrigger }: ThreadsSidebarProps) {
  const scope = useScopeContext();
  const [threads, setThreads] = useState<ThreadItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  const [newThreadTitle, setNewThreadTitle] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);

  // Focus input when renaming starts
  useEffect(() => {
    if (renamingThreadId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingThreadId]);

  useEffect(() => {
    loadThreads();
  }, [agentId]);

  // Refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      loadThreads();
    }
  }, [refreshTrigger]);

  const loadThreads = async () => {
    try {
      setLoading(true);
      console.log('[ThreadsSidebar] Loading threads for agent:', agentId);
      const response = await threadsService.getThreads(scope.teamId, {
        agentId,
        orderBy: 'updatedAt',
        sortDirection: 'DESC',
      });
      console.log('[ThreadsSidebar] Loaded threads:', response.threads?.length || 0);
      setThreads(response.threads || []);
    } catch (err) {
      console.error('[ThreadsSidebar] Failed to load threads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewChat = () => {
    const guid = crypto.randomUUID ? crypto.randomUUID() :
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    const timestamp = Date.now();
    const threadId = `${agentId}-${guid}-${timestamp}`;
    route(`${scope.getNavigationPrefix()}/chat/${agentId}/${threadId}`);
  };

  const groupThreadsByTime = (): GroupedThreads => {
    const now = new Date();

    // Start of today (midnight)
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    // Start of yesterday (midnight yesterday)
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);

    // 7 days ago from start of today
    const sevenDaysAgo = new Date(todayStart);
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

    // 30 days ago from start of today
    const thirtyDaysAgo = new Date(todayStart);
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    return threads.reduce((acc, thread) => {
      const threadDate = new Date(thread.lastActivity);

      if (threadDate >= todayStart) {
        acc.today.push(thread);
      } else if (threadDate >= yesterdayStart) {
        acc.yesterday.push(thread);
      } else if (threadDate >= sevenDaysAgo) {
        acc.last7Days.push(thread);
      } else {
        // All older threads go into last30Days regardless of actual age
        acc.last30Days.push(thread);
      }

      return acc;
    }, { today: [], yesterday: [], last7Days: [], last30Days: [] } as GroupedThreads);
  };

  const groupedThreads = groupThreadsByTime();

  const getThreadTitle = (thread: ThreadItem): string => {
    return thread.title || 'Untitled';
  };

  const handleRenameThread = async (threadId: string, newTitle: string) => {
    try {
      await threadsService.updateThread(scope.teamId, threadId, { title: newTitle });
      await loadThreads();
      setRenamingThreadId(null);
    } catch (err) {
      console.error('Failed to rename thread:', err);
    }
  };

  const startRename = (thread: ThreadItem) => {
    setNewThreadTitle(getThreadTitle(thread));
    setRenamingThreadId(thread.threadId);
  };

  // Render a single thread item - using inline JSX to avoid component remounting
  const renderThreadItem = (thread: ThreadItem) => {
    const isRenaming = renamingThreadId === thread.threadId;
    const isActive = currentThreadId === thread.threadId;

    return (
      <li key={thread.threadId}>
        <div class={`flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-base-content/10 transition-colors ${isActive ? 'bg-base-content/15' : ''}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          {isRenaming ? (
            <input
              ref={renameInputRef}
              type="text"
              value={newThreadTitle}
              onInput={(e) => setNewThreadTitle((e.target as HTMLInputElement).value)}
              onBlur={() => {
                if (newThreadTitle.trim()) {
                  handleRenameThread(thread.threadId, newThreadTitle);
                } else {
                  setRenamingThreadId(null);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newThreadTitle.trim()) {
                  handleRenameThread(thread.threadId, newThreadTitle);
                } else if (e.key === 'Escape') {
                  setRenamingThreadId(null);
                }
              }}
              class="input input-sm input-bordered flex-1 bg-base-100"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <a
              href={`${scope.getNavigationPrefix()}/chat/${agentId}/${thread.threadId}`}
              class="flex-1 truncate"
            >
              {getThreadTitle(thread)}
            </a>
          )}
          <div class="dropdown dropdown-end">
            <button
              tabIndex={0}
              class="btn btn-ghost btn-xs btn-square opacity-60 hover:opacity-100"
              onClick={(e) => e.stopPropagation()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                />
              </svg>
            </button>
            <ul tabIndex={0} class="dropdown-content menu p-2 shadow bg-base-100 rounded-box w-40 z-50">
              <li>
                <a
                  onClick={(e) => {
                    e.preventDefault();
                    startRename(thread);
                  }}
                  class="text-base-content"
                >
                  Rename
                </a>
              </li>
            </ul>
          </div>
        </div>
      </li>
    );
  };

  return (
    <div class="flex flex-col h-full flex-1 text-sm">
      {/* New Chat Button */}
      <div class="p-4 border-b border-base-content/10">
        <button
          onClick={handleNewChat}
          class="btn btn-block btn-primary"
        >
          + New Chat
        </button>
      </div>

      {/* Thread History */}
      <div class="flex-1 overflow-y-auto px-4 pb-4">
        <h3 class="text-sm font-semibold mb-3 px-2 opacity-80">History</h3>

        {loading ? (
          <div class="flex justify-center py-8">
            <span class="loading loading-spinner loading-md"></span>
          </div>
        ) : (
          <div class="space-y-4">
            {/* Today */}
            {groupedThreads.today.length > 0 && (
              <div>
                <h4 class="text-xs font-semibold mb-2 px-2 opacity-60">Today</h4>
                <ul class="space-y-1">
                  {groupedThreads.today.map(renderThreadItem)}
                </ul>
              </div>
            )}

            {/* Yesterday */}
            {groupedThreads.yesterday.length > 0 && (
              <div>
                <h4 class="text-xs font-semibold mb-2 px-2 opacity-60">Yesterday</h4>
                <ul class="space-y-1">
                  {groupedThreads.yesterday.map(renderThreadItem)}
                </ul>
              </div>
            )}

            {/* Last 7 Days */}
            {groupedThreads.last7Days.length > 0 && (
              <div>
                <h4 class="text-xs font-semibold mb-2 px-2 opacity-60">Last 7 Days</h4>
                <ul class="space-y-1">
                  {groupedThreads.last7Days.map(renderThreadItem)}
                </ul>
              </div>
            )}

            {/* Older */}
            {groupedThreads.last30Days.length > 0 && (
              <div>
                <h4 class="text-xs font-semibold mb-2 px-2 opacity-60">Older</h4>
                <ul class="space-y-1">
                  {groupedThreads.last30Days.map(renderThreadItem)}
                </ul>
              </div>
            )}

            {threads.length === 0 && !loading && (
              <div class="text-center py-8 text-base-content/60">
                <p class="text-sm">No conversations yet</p>
                <p class="text-xs mt-1">Start a new chat above</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
